import { IconLock } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from './format.js';
import classes from './master-section.module.css';
import { StatusBadge } from './StatusBadge.js';

/**
 * Kaptana ait bölümü yazma sıfatı (ADR 0127 §2, §3): `master`, `deputy` ("on behalf of Master")
 * ya da `null`. Eski gemi sunucusu göndermez (`undefined`): ekran kilitlemez, yazma yolu denetler.
 */
export type MasterCapacity = 'master' | 'deputy' | null;

/** Bölümü son yazan, o günkü gemi rütbesi ve vekil izi (ADR 0127 §4). */
export interface MasterSectionAuthor {
  userId: string;
  name: string | null;
  roleId: string | null;
  roleName: string | null;
  at: string;
  tzOffsetMin: number;
  onBehalf: boolean;
}

/** Kişi bu bölümü yazamaz mı: yalnız sunucu `null` dediyse (eski sunucuda kilit yok). */
export function masterLocked(capacity: MasterCapacity | undefined): boolean {
  return capacity === null;
}

export interface MasterSectionNoteProps {
  /** Gemide kişinin sıfatı; ofiste verilmez. */
  capacity?: MasterCapacity | undefined;
  author?: MasterSectionAuthor | null | undefined;
  /** Bölüm zaten salt okunursa (kapalı kayıt, ofis) kilit nedeni yazılmaz. */
  editable?: boolean;
}

/**
 * Kaptan bölümünün altındaki not (ADR 0127 §2–§4): yazan, rütbesi ve zamanı; vekilse "On behalf
 * of Master" rozeti; kişi yazamıyorsa kilit nedeni; vekil yazıyorsa uyarı.
 */
export function MasterSectionNote({ capacity, author, editable = true }: MasterSectionNoteProps) {
  const { t } = useTranslation();
  const m = (key: string, options: Record<string, unknown> = {}) =>
    t(`masterSection.${key}`, options);
  const locked = editable && masterLocked(capacity);
  if (!author && !locked && !(editable && capacity === 'deputy')) return null;
  const badge = <StatusBadge tone="warning">{m('onBehalf')}</StatusBadge>;
  return (
    <div data-master-section="true" className={classes.note}>
      {author ? (
        <p data-master-author="true" className={classes.line}>
          <span>
            {m('writtenBy', {
              name: author.name ?? '—',
              rank: author.roleName ?? m('noRank'),
              at: formatDateTime(author.at, author.tzOffsetMin),
            })}
          </span>
          {author.onBehalf ? badge : null}
        </p>
      ) : null}
      {locked ? (
        <p data-master-locked="true" className={classes.line}>
          <IconLock size={14} aria-hidden className={classes.icon} />
          <span>{m('locked')}</span>
        </p>
      ) : editable && capacity === 'deputy' ? (
        <p data-master-deputy="true" className={classes.line}>
          {badge}
          <span>{m('deputy')}</span>
        </p>
      ) : null}
    </div>
  );
}
